import React, { useEffect, useState } from "react";
import useAxios from "../../hooks/useAxios";
import { useSelector } from "react-redux";
import {
  formatIsoDate,
  renderStatus,
  formatName,
} from "../../utils/generalUtils";
import GeneratePdf from "../../generatePdf/GeneratePdf";
import { Select, Space } from "antd";
import SpectatorTable from "../../components/Table/SpectatorTable.js";

const SpectateTRLocal = () => {
  const { response, loading, fetchData } = useAxios();
  const { spectatorTrlPopup } = useSelector((state) => state.generalSlice);
  const { spectator_accesses } = useSelector(
    (state) => state.accountDetailSlice.details
  );

  const handleRefresh = (positionId) => {
    fetchData({ url: `/approver/trl/${positionId}`, method: "GET" });
  };

  // A Dean grant scoped "facsec" (Faculty Secretary View) is excluded —
  // that scope means FSR only, not TRL/COS/CWA.
  const mappedEmpPos = (spectator_accesses || [])
    .filter((item) => item?.scope !== "facsec")
    .map((item) => ({
      value: item?.approver_position_id,
      label: item?.approver_position
        ? `${item.approver_position.position_name} - ${item.approver_position.unit}`
        : `Position ${item?.approver_position_id}`,
    }));
  const hasActiveSpectatorPosition = mappedEmpPos.length > 0;

  const [selectedPos, setSelectedPos] = useState(
    mappedEmpPos.length > 0 ? mappedEmpPos[0].value : undefined
  );

  useEffect(() => {
    if (
      (selectedPos === undefined || selectedPos === null) &&
      mappedEmpPos.length > 0
    ) {
      setSelectedPos(mappedEmpPos[0].value);
    }
  }, [mappedEmpPos, selectedPos]);

  const handlePosSelect = (posId) => {
    setSelectedPos(posId);
  };

  useEffect(() => {
    if (selectedPos === undefined || selectedPos === null) {
      return;
    }

    handleRefresh(selectedPos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPos]);

  const mappedData =
    response?.data?.map((item, index) => ({
      key: index + 1,
      name: `${item?.submission?.submitter?.last_name || ""}, ${
        item?.submission?.submitter?.first_name || ""
      }`.trim(),
      formattedName: formatName(
        item?.submission?.submitter?.first_name,
        item?.submission?.submitter?.middle_name,
        item?.submission?.submitter?.last_name,
        "lfmcut"
      ),
      approver_name: `${item?.approver?.last_name || ""}, ${
        item?.approver?.first_name || ""
      }`.trim(),
      formattedApproverName: formatName(
        item?.approver?.first_name,
        item?.approver?.middle_name,
        item?.approver?.last_name,
        "lfmcut"
      ),
      ...item?.submission?.travel_request_local,
      date_submitted: item?.submission?.created_at
        ? formatIsoDate(item?.submission?.created_at)
        : null,
      submitter_position: item?.submission?.position?.position_name,
      remarks: item?.submission?.remarks || null,
      status: item?.status || "no data",
      submission_id: item?.submission?.id,
      approval_id: item?.id,
      approval_date: item?.approval_date,
    })) || [];

  //columns for table
  const columns = [
    {
      title: "No.",
      dataIndex: "key", width: 60,
      rowScope: "row",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Destination",
      dataIndex: "destination",
      key: "destination",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => {
        return (
          <GeneratePdf
            docuType="trl"
            contents={item}
            disabled={item?.status !== "approved"}
          />
        );
      },
    },
  ];

  const viewingDataConfig = [
    {
      //  NOTE: MAKE THE KEY TO 1 IN SENDING THE PROPS IF YOU WANT IT ACTIVE, 0 OTHERWISE
      key: 1,
      collapseName: "Required Details",
      collapseItems: [
        {
          key: 1,
          name: "name",
          label: "Requester",
        },
        {
          key: 2,
          name: "start_date",
          label: "Start Date",
        },
        {
          key: 3,
          name: "end_date",
          label: "End Date",
        },
        {
          key: 4,
          name: "destination",
          label: "Destination",
        },
        {
          key: 5,
          name: "purpose",
          label: "Purpose of Travel",
        },
      ],
    },
    {
      //  NOTE: MAKE THE KEY TO 1 IN SENDING THE PROPS IF YOU WANT IT ACTIVE, 0 OTHERWISE
      key: 1,
      collapseName: "Additional Details",
      collapseItems: [
        {
          key: 1,
          name: "expenses",
          label: "Estimated Expenses",
        },
        {
          key: 2,
          name: "funding_sourse",
          label: "Funding Source",
        },
        {
          key: 3,
          name: "local_travel_transportation",
          label: "Local Travel Transportation",
        },
        {
          key: 4,
          name: "entitlement",
          label: "Entitlement",
        },
      ],
    },
    {
      //  NOTE: MAKE THE KEY TO 1 IN SENDING THE PROPS IF YOU WANT IT ACTIVE, 0 OTHERWISE
      key: 1,
      collapseName: "Approver's Remarks",
      collapseItems: [
        {
          key: 1,
          name: "remarks",
          label: "Remarks",
        },
      ],
    },
  ];

  return (
    <>
      {loading && "loading..."}
      {!loading && (
        <>
          <Space>
            <p>Spectate: </p>
            <Select
              value={selectedPos}
              options={mappedEmpPos}
              onChange={handlePosSelect}
              disabled={!hasActiveSpectatorPosition}
              popupMatchSelectWidth={false}
            />
          </Space>
          {!hasActiveSpectatorPosition && (
            <p style={{ marginTop: 8, color: "#8c8c8c" }}>
              No active spectator position available.
            </p>
          )}
          <SpectatorTable
            tableColumn={columns}
            tableData={mappedData}
            popupSlice={spectatorTrlPopup}
            popupName="spectatorTrlPopup"
            docuType="Local Travel Request"
            viewingDataConfig={viewingDataConfig}
          />
        </>
      )}
    </>
  );
};

export default SpectateTRLocal;
